import { GameBoardEntry } from "../../services/MinigameService";

export interface BoardOption {
    /** This option's part of the API's variant key. */
    value: string;
    /** i18n key (pages namespace) for the toggle label. */
    labelKey: string;
}

/**
 * One game on the games leaderboard. A game is listed here once it is played as a server-scored
 * run (tasks/games-leaderboard.md) — the backend refuses boards it doesn't know.
 */
export interface GameBoardConfig {
    /** The API's game key. */
    game: string;
    /** gamesHub.games.<key> holds the title. */
    key: string;
    link: string;
    accent: string;
    /** Text colour on top of the accent (the active toggle). */
    accentInk: string;
    /**
     * The settings that split the game into boards, one toggle each. The API's variant key is the
     * chosen values joined with "-", e.g. Rating Duel's "five-hard".
     */
    dimensions: BoardOption[][];
    /** i18n key for the score column. */
    scoreLabelKey: string;
    formatScore: (entry: GameBoardEntry) => string;
    /** i18n key for a line explaining how the board is scored, when it isn't a plain streak. */
    noteKey?: string;
}

const DIFFICULTY: BoardOption[] = [
    { value: "easy", labelKey: "gamesLeaderboard.easy" },
    { value: "hard", labelKey: "gamesLeaderboard.hard" },
];

export const GAME_BOARDS: GameBoardConfig[] = [
    {
        // Daily, so one board across all four division groups: points per month.
        game: "powerstrikle",
        key: "powerstrikle",
        link: "/powerstrikle",
        accent: "#f15c25",
        accentInk: "#fff",
        dimensions: [[{ value: "all", labelKey: "gamesLeaderboard.allDivisions" }]],
        scoreLabelKey: "gamesLeaderboard.points",
        formatScore: (e) => String(e.score),
        noteKey: "gamesLeaderboard.powerstrikleNote",
    },
    {
        game: "whos-that-pro",
        key: "whosThatPro",
        link: "/whos-that-pro",
        accent: "#4aa3ff",
        accentInk: "#0a1420",
        dimensions: [DIFFICULTY],
        scoreLabelKey: "gamesLeaderboard.streak",
        formatScore: (e) => String(e.score),
    },
    {
        game: "team-roster",
        key: "teamRoster",
        link: "/team-roster",
        accent: "#4ecbe0",
        accentInk: "#0f1a1c",
        dimensions: [DIFFICULTY],
        scoreLabelKey: "gamesLeaderboard.streak",
        formatScore: (e) => String(e.score),
    },
    {
        game: "rating-duel",
        key: "ratingDuel",
        link: "/rating-duel",
        accent: "#4ade80",
        accentInk: "#0b1f12",
        dimensions: [
            [
                { value: "duel", labelKey: "ratingDuel.modeDuel" },
                { value: "five", labelKey: "ratingDuel.modeFive" },
            ],
            DIFFICULTY,
        ],
        scoreLabelKey: "gamesLeaderboard.streak",
        formatScore: (e) => String(e.score),
    },
    {
        game: "logo-puzzle",
        key: "logoPuzzle",
        link: "/logo-puzzle",
        accent: "#c084fc",
        accentInk: "#1a0e26",
        dimensions: [DIFFICULTY],
        // The score is the solve time in milliseconds; the fastest ranks first.
        scoreLabelKey: "gamesLeaderboard.time",
        formatScore: (e) => fmtSolveTime(e.score),
        noteKey: "gamesLeaderboard.logoNote",
    },
];

/** A solve time as m:ss.t — tenths, so close times don't read as ties. */
export function fmtSolveTime(ms: number): string {
    const tenths = Math.floor(ms / 100);
    const m = Math.floor(tenths / 600);
    const s = Math.floor((tenths % 600) / 10);
    return `${m}:${String(s).padStart(2, "0")}.${tenths % 10}`;
}

export const boardConfig = (game: string | null | undefined): GameBoardConfig | undefined =>
    GAME_BOARDS.find((b) => b.game === game);

/** The chosen value per dimension, read from a variant key; unknown parts fall back to the first. */
export const parseVariant = (config: GameBoardConfig, variant: string | null | undefined): string[] => {
    const parts = (variant ?? "").split("-");
    return config.dimensions.map((dim, i) => dim.find((o) => o.value === parts[i])?.value ?? dim[0].value);
};
